// Push-to-talk mic capture for the (currently plumbing-only) voice command
// path. Mirrors RemoteButton's pointer-event pattern, but captures audio via
// getUserMedia/MediaRecorder instead of dispatching keypresses, and hands
// chunks to Webrtc.js as a "voice_control" CustomEvent — see
// Webrtc.js#sendVoiceControl for the wire format sent over the existing
// RTCDataChannel.
// Capsule geometry from the heroicons microphone glyph (viewBox 0 0 24 24):
// the pill body spans x:[9,15], y:[4.5,15.75].
const MIC_PILL_TOP = 4.5;
const MIC_PILL_BOTTOM = 15.75;
const MIC_PILL_HEIGHT = MIC_PILL_BOTTOM - MIC_PILL_TOP;

export const VoiceButton = {
  mounted() {
    this.recording = false;
    this.mediaRecorder = null;
    this.stream = null;
    this.audioContext = null;
    this.analyser = null;
    this.levelRafId = null;
    this.levelFillEl = this.el.querySelector(".mic-level-fill");

    const boundHandleDown = this.handleDown.bind(this);
    const boundHandleUp = this.handleUp.bind(this);

    this.el.addEventListener("pointerdown", boundHandleDown);
    this.el.addEventListener("pointerup", boundHandleUp);
    this.el.addEventListener("pointercancel", boundHandleUp);
    this.el.addEventListener("pointerleave", boundHandleUp);

    this.cleanup = () => {
      this.el.removeEventListener("pointerdown", boundHandleDown);
      this.el.removeEventListener("pointerup", boundHandleUp);
      this.el.removeEventListener("pointercancel", boundHandleUp);
      this.el.removeEventListener("pointerleave", boundHandleUp);
      this.stopRecording();
    };
  },

  destroyed() {
    this.cleanup?.();
  },

  async handleDown(_event) {
    if (this.recording) return;
    if (this.el.disabled || this.el.getAttribute("aria-disabled") === "true") return;

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      console.warn("Voice command: microphone access denied or unavailable", error);
      return;
    }

    this.stream = stream;
    this.recording = true;
    this.dispatchVoiceEvent({ action: "start" });
    this.startLevelMeter(stream);

    const mediaRecorder = new MediaRecorder(stream);
    mediaRecorder.ondataavailable = async (event) => {
      if (event.data.size === 0) return;
      const data = await blobToBase64(event.data);
      this.dispatchVoiceEvent({ action: "chunk", data });
    };
    mediaRecorder.onstop = () => this.dispatchVoiceEvent({ action: "end" });

    this.mediaRecorder = mediaRecorder;
    mediaRecorder.start(250);
  },

  handleUp(_event) {
    this.stopRecording();
  },

  stopRecording() {
    if (!this.recording) return;

    this.recording = false;
    this.mediaRecorder?.stop();
    this.mediaRecorder = null;

    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;

    this.stopLevelMeter();
  },

  startLevelMeter(stream) {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass || !this.levelFillEl) return;

    this.audioContext = new AudioContextClass();
    const source = this.audioContext.createMediaStreamSource(stream);
    const analyser = this.audioContext.createAnalyser();
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.6;
    source.connect(analyser);
    this.analyser = analyser;

    const samples = new Uint8Array(analyser.fftSize);

    const tick = () => {
      analyser.getByteTimeDomainData(samples);

      let sumSquares = 0;
      for (let i = 0; i < samples.length; i++) {
        const deviation = (samples[i] - 128) / 128;
        sumSquares += deviation * deviation;
      }
      const rms = Math.sqrt(sumSquares / samples.length);
      const level = Math.min(1, rms * 4);

      this.setLevelFill(level);
      this.levelRafId = requestAnimationFrame(tick);
    };

    this.levelRafId = requestAnimationFrame(tick);
  },

  stopLevelMeter() {
    if (this.levelRafId) {
      cancelAnimationFrame(this.levelRafId);
      this.levelRafId = null;
    }

    this.analyser = null;
    this.audioContext?.close();
    this.audioContext = null;
    this.setLevelFill(0);
  },

  setLevelFill(level) {
    if (!this.levelFillEl) return;

    const filledHeight = MIC_PILL_HEIGHT * level;
    this.levelFillEl.setAttribute("y", MIC_PILL_BOTTOM - filledHeight);
    this.levelFillEl.setAttribute("height", filledHeight);

    const hue = 120 * (1 - level);
    this.levelFillEl.setAttribute("fill", `hsl(${hue}, 75%, 45%)`);
  },

  dispatchVoiceEvent(detail) {
    const targetElement = document.querySelector(this.el.dataset.dispatchTo);
    if (targetElement) {
      targetElement.dispatchEvent(new CustomEvent("voice_control", { detail }));
    }
  },
};

async function blobToBase64(blob) {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}
